import React, { useEffect, useState } from 'react';

interface GlobalLoadingSpinnerProps {
  isLoading: boolean;
  onLoadComplete?: () => void;
}

export const GlobalLoadingSpinner: React.FC<GlobalLoadingSpinnerProps> = ({ 
  isLoading,
  onLoadComplete 
}) => {
  const [shouldShow, setShouldShow] = useState(true);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (!isLoading && shouldShow) {
      // Trigger fade out animation
      setIsFading(true);
      // Remove from DOM after animation completes
      const timer = setTimeout(() => {
        setShouldShow(false);
        onLoadComplete?.();
      }, 800); // Match the CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [isLoading, shouldShow, onLoadComplete]);

  if (!shouldShow) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 transition-opacity duration-700 ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Animated background grid */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_24%,rgba(120,119,198,.05)_25%,rgba(120,119,198,.05)_26%,transparent_27%,transparent_74%,rgba(120,119,198,.05)_75%,rgba(120,119,198,.05)_76%,transparent_77%,transparent),linear-gradient(90deg,transparent_24%,rgba(120,119,198,.05)_25%,rgba(120,119,198,.05)_26%,transparent_27%,transparent_74%,rgba(120,119,198,.05)_75%,rgba(120,119,198,.05)_76%,transparent_77%,transparent)] bg-[length:50px_50px]" />
        </div>
      </div>

      {/* Content container */}
      <div className="relative z-10 flex flex-col items-center justify-center gap-8">
        {/* Animated spinner */}
        <div className="relative w-24 h-24">
          {/* Outer rotating ring */}
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-400 border-r-blue-300 animate-spin" />
          
          {/* Middle rotating ring (opposite direction) */}
          <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-rose-400 border-r-rose-300 animate-spin" style={{ animationDirection: 'reverse' }} />
          
          {/* Inner pulsing circle */}
          <div className="absolute inset-4 rounded-full bg-gradient-to-br from-red-500 to-rose-600 opacity-30 animate-pulse" />
          
          {/* Center dot */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          </div>
        </div>

        {/* Loading text with animation */}
        <div className="text-center space-y-3">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-red-400 via-rose-400 to-red-400 bg-clip-text text-transparent animate-pulse">
            Please wait...
          </h2>
          <p className="text-slate-300 text-sm font-medium">System is loading</p>
          
          {/* Animated dots */}
          <div className="flex justify-center gap-1 pt-2">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0s' }} />
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>

        {/* Progress bar (subtle) */}
        <div className="w-64 h-1 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-red-400 to-rose-400 rounded-full animate-[shimmer_2s_infinite]" 
            style={{
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite'
            }}
          />
        </div>
      </div>

      {/* Subtle glow effect */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-10 animate-pulse" />
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default GlobalLoadingSpinner;
